/**
 * System Settings, as the Materials screens read them. 29-09-2026, migration 0184.
 *
 * The business rules the System Administrator maintains live on the one row
 * of company_profile. That row is already downloaded at login and kept for
 * offline use (primeMasterData, cache name 'company_profile'), so this reads
 * it under the SAME cache name: a store-man's tablet with no signal still
 * knows the tolerance.
 *
 * Every getter falls back to the number that was written into the code before
 * the setting existed. A device that cannot read the row, or a database that
 * does not have the column yet, behaves exactly as it always did.
 *
 * Materials only. The Service side reads service_settings, its own table, on
 * its own pages - the two modules share company_profile and nothing else.
 */
import { supabase } from '@/lib/supabase'
import { useQuery } from '@/lib/data'
import { GRN_TOLERANCE_PCT } from '@/lib/domain'

/** Today's numbers. Also the defaults of the database columns. */
export const SETTING_DEFAULTS = {
  grnTolerancePct: GRN_TOLERANCE_PCT,   // company_profile.grn_tolerance_pct
  payableOverdueDays: 45,               // company_profile.payable_overdue_days
  invoiceTolerancePct: 5,               // company_profile.invoice_tolerance_pct
  // Phase 2, 15-10-2026, migration 0212.
  itcWarningDays: 150,                  // company_profile.itc_warning_days (the law's 180 is fixed)
  staleRateDays: 180,                   // company_profile.stale_rate_days
  vendorConcentrationPct: 40,           // company_profile.vendor_concentration_pct
  lateEntryWarnDays: 30,                // company_profile.late_entry_warn_days
  lateEntrySeriousDays: 90,             // company_profile.late_entry_serious_days
  poValidityDefaultDays: 7,             // company_profile.po_validity_default_days
  materialMinStockDefault: 10,          // company_profile.material_min_stock_default
  materialMaxStockDefault: 1000,        // company_profile.material_max_stock_default
} as const

const num = (v: unknown, fallback: number) => {
  const n = Number(v)
  return v === null || v === undefined || v === '' || !Number.isFinite(n) ? fallback : n
}

export function useCompanySettings() {
  const { data, loading } = useQuery<any>('company_profile',
    () => supabase.from('company_profile').select('*').eq('id', 1))
  const row = data[0]
  return {
    loading,
    /** 0212: the row has actually been read (from the server or the cache). */
    ready: Boolean(row),
    grnTolerancePct: num(row?.grn_tolerance_pct, SETTING_DEFAULTS.grnTolerancePct),
    payableOverdueDays: num(row?.payable_overdue_days, SETTING_DEFAULTS.payableOverdueDays),
    invoiceTolerancePct: num(row?.invoice_tolerance_pct, SETTING_DEFAULTS.invoiceTolerancePct),
    itcWarningDays: num(row?.itc_warning_days, SETTING_DEFAULTS.itcWarningDays),
    staleRateDays: num(row?.stale_rate_days, SETTING_DEFAULTS.staleRateDays),
    vendorConcentrationPct: num(row?.vendor_concentration_pct, SETTING_DEFAULTS.vendorConcentrationPct),
    lateEntryWarnDays: num(row?.late_entry_warn_days, SETTING_DEFAULTS.lateEntryWarnDays),
    lateEntrySeriousDays: num(row?.late_entry_serious_days, SETTING_DEFAULTS.lateEntrySeriousDays),
    poValidityDefaultDays: num(row?.po_validity_default_days, SETTING_DEFAULTS.poValidityDefaultDays),
    materialMinStockDefault: num(row?.material_min_stock_default, SETTING_DEFAULTS.materialMinStockDefault),
    materialMaxStockDefault: num(row?.material_max_stock_default, SETTING_DEFAULTS.materialMaxStockDefault),
  }
}
